import React, { useState, useEffect, useCallback } from 'react';
import { Download, MoreVertical, Eye, Receipt, Flag, Search, RefreshCw, AlertCircle } from 'lucide-react';
import Button from './Button';
import { apiService } from '../../services/api';
import type { AdminTransaction } from '../../services/api';
import './TransactionTable.css';

interface ActionDropdownProps {
  transactionId: string;
}

const ActionDropdown: React.FC<ActionDropdownProps> = ({ transactionId }) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleAction = (action: string) => {
    console.log(`${action} for transaction ${transactionId}`);
    setIsOpen(false);
  };

  return (
    <div className="action-dropdown">
      <button
        className="action-trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="More actions"
      >
        <MoreVertical size={16} />
      </button>

      {isOpen && (
        <>
          <div className="dropdown-overlay" onClick={() => setIsOpen(false)} />
          <div className="dropdown-menu">
            <button
              className="dropdown-item"
              onClick={() => handleAction('view-details')}
            >
              <Eye size={14} />
              View Details
            </button>
            <button
              className="dropdown-item"
              onClick={() => handleAction('download-receipt')}
            >
              <Receipt size={14} />
              Download Receipt
            </button>
            <button
              className="dropdown-item flag-transaction"
              onClick={() => handleAction('flag-transaction')}
            >
              <Flag size={14} />
              Flag Transaction
            </button>
          </div>
        </>
      )}
    </div>
  );
};

interface TransactionTableProps {
  hideHeader?: boolean;
}

const TransactionTable: React.FC<TransactionTableProps> = ({ hideHeader = false }) => {
  const [transactions, setTransactions] = useState<AdminTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getAdminTransactions({
        page,
        limit,
        // We'll add filter support here later if needed
      });
      setTransactions(data.data);
      setTotal(data.total);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch transactions');
      console.error('Error fetching transactions:', err);
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleRefresh = () => {
    setPage(1);
    fetchTransactions();
  };

  return (
    <div className="transaction-table-container">
      {!hideHeader && (
        <div className="table-header">
          <h3 className="table-title">Transaction Logs</h3>
          <div className="table-actions">
            <Button variant="outline" size="sm" onClick={handleRefresh} disabled={loading}>
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              Refresh
            </Button>
            <Button variant="outline" size="sm">
              <Download size={16} />
              CSV Export
            </Button>
          </div>
        </div>
      )}

      <div className="table-controls">
        <div className="table-search">
          <Search size={16} className="search-icon" />
          <input
            type="text"
            placeholder="Search transactions..."
            className="table-search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="table-pagination-info">
          Showing {transactions.length} of {total} results
        </div>
      </div>

      {error ? (
        <div className="table-error-state">
          <AlertCircle size={48} />
          <p>{error}</p>
          <Button variant="primary" size="sm" onClick={fetchTransactions}>Retry</Button>
        </div>
      ) : (
        <div className="table-wrapper">
          <table className="transaction-table">
            <thead>
              <tr>
                <th>Reference</th>
                <th>Company</th>
                <th>User / Card</th>
                <th>Type</th>
                <th>Date & Time</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="loading-state">
                    <div className="spinner" />
                    Loading transactions...
                  </td>
                </tr>
              ) : transactions.length === 0 ? (
                <tr>
                  <td colSpan={8} className="empty-state">No transactions found</td>
                </tr>
              ) : (
                transactions.map((tran) => (
                  <tr key={tran.id}>
                    <td><span className="reference-id">{tran.reference}</span></td>
                    <td><span className="company-name">{tran.user?.company?.legalName || 'N/A'}</span></td>
                    <td>
                      <div className="user-info">
                        <span className="user-name">{tran.user ? `${tran.user.firstName} ${tran.user.lastName}` : 'System'}</span>
                        <span className="user-email">{tran.user?.email || ''}</span>
                      </div>
                    </td>
                    <td><span className="transaction-type">{tran.type.replace(/_/g, ' ')}</span></td>
                    <td><span className="datetime">{formatDate(tran.createdAt)}</span></td>
                    <td className="amount">{formatCurrency(tran.amount)}</td>
                    <td>
                      <span className={`status status--${tran.status.toLowerCase()}`}>
                        {tran.status}
                      </span>
                    </td>
                    <td>
                      <ActionDropdown transactionId={tran.id} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {!loading && !error && total > limit && (
        <div className="pagination-footer">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            Previous
          </Button>
          <span className="page-indicator">Page {page} of {Math.ceil(total / limit)}</span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= Math.ceil(total / limit)}
            onClick={() => setPage(page + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
};

export default TransactionTable;
